'use client';
import { useContext } from 'react';
import Link from 'next/link';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { Input } from '@/components/Input';
import { TextArea } from '@/components/TextArea';
import Button from '@/components/Button';
import { cn } from '@/lib/utils';
import { formFields } from '@/lib/pardot/pardot-data';
import { trackFathomEvent } from '@/lib/fathom/trackFathomEvent';
import { WORK_EMAIL_NOT_SENT } from '@/lib/forms/is-work-email';
import { TermsAndConditionsCheckbox, TermsAndPrivacyCheckbox } from '@/sections/trial/hero/TermsAndConditionsCheckbox';
import { FormContext } from './index';
import './FormFields.css';

import { TURNSTILE_ERROR_ID, TURNSTILE_PROMPT_ID } from './TurnstileField';

export { TurnstileField } from './TurnstileField';

interface FormFieldProps {
  className?: string;
  required?: boolean;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

export function EmailField({ className = '', placeholder = 'Work email', required = true, inputRef }: FormFieldProps) {
  // Field-level work-email warning: rendered right under the email input (not at the
  // bottom of the form) so a blocked submit's feedback is where the user is looking.
  // role="alert" (on the warning text only, which is also all the input describes) keeps
  // the announcement short; ContactForm also moves focus here. Styled as an error (red
  // ring + icon), not helper text: a blocked submit only jumps back to this field, so the
  // cue has to be unmissable on a phone.
  const { emailWarning, emailBlocked, buildScanClickEvent } = useContext(FormContext);
  return (
    <>
      <Input
        inputRef={inputRef}
        name={formFields.email}
        placeholder={placeholder}
        className={cn(
          `form-animate-in col-span-2 bg-(--form-input-bg,#2D4453)`,
          className,
          emailWarning && 'ring-2 ring-core-error',
        )}
        type="email"
        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
        required={required}
        aria-invalid={emailWarning ? true : undefined}
        aria-describedby={emailWarning ? 'work-email-warning' : undefined}
      />
      {emailWarning && (
        <div
          data-testid="work-email-warning"
          className="col-span-2 -mt-1 flex items-start gap-2 text-sm text-core-error"
        >
          <ExclamationCircleIcon className="mt-px size-5 shrink-0" aria-hidden="true" />
          <div className="flex flex-col gap-1">
            <p id="work-email-warning" role="alert">
              {emailWarning}
            </p>
            {emailBlocked && (
              <p data-testid="work-email-fallback" className="text-white" role="status">
                {WORK_EMAIL_NOT_SENT} No work email?{' '}
                <Link
                  href="/scans/gradle/"
                  className="underline"
                  onClick={() => buildScanClickEvent && trackFathomEvent(buildScanClickEvent)}
                >
                  Run a free Build Scan®
                </Link>{' '}
                instead.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function NameFields({ className = '', required = true }: FormFieldProps) {
  return (
    <>
      <Input
        name={formFields.first_name}
        placeholder="First name"
        className={cn(`form-animate-in col-span-1 bg-(--form-input-bg,#2D4453)`, className)}
        required={required}
      />
      <Input
        name={formFields.last_name}
        placeholder="Last name"
        className={cn(`form-animate-in col-span-1 bg-(--form-input-bg,#2D4453)`, className)}
        required={required}
      />
    </>
  );
}

export function CompanyField({ className = '', placeholder = 'Company name', required = true }: FormFieldProps) {
  return (
    <Input
      name={formFields.company}
      placeholder={placeholder}
      className={cn(`form-animate-in col-span-2 bg-(--form-input-bg,#2D4453)`, className)}
      required={required}
    />
  );
}

export function JobTitleField({ className = '', placeholder = 'Job title', required = true }: FormFieldProps) {
  return (
    <Input
      name={formFields.job_title}
      placeholder={placeholder}
      className={cn(`form-animate-in col-span-2 bg-(--form-input-bg,#2D4453)`, className)}
      required={required}
    />
  );
}

interface InterestFieldProps extends FormFieldProps {
  placeholder?: string;
  name?: string;
}

export function InterestField({
  className = '',
  placeholder = 'What are you most interested in observing?',
  name = formFields.message,
  required = true,
}: InterestFieldProps) {
  return (
    <TextArea
      name={name}
      placeholder={placeholder}
      className={cn(`form-animate-in col-span-2 bg-(--form-input-bg,#2D4453)`, className)}
      required={required}
    />
  );
}

export function TermsCheckbox({
  className = '',
  termsAndPrivacy = true,
}: {
  className?: string;
  termsAndPrivacy?: boolean;
}) {
  return (
    <div className={cn(`form-animate-in col-span-2 flex items-start gap-2`, className)}>
      <input type="checkbox" id="hero-terms-checkbox" className="mt-1 size-4" required />
      {termsAndPrivacy ? (
        <TermsAndPrivacyCheckbox forId="hero-terms-checkbox" />
      ) : (
        <TermsAndConditionsCheckbox forId="hero-terms-checkbox" />
      )}
    </div>
  );
}

interface SubmitButtonProps {
  text?: string;
  className?: string;
}

/** Submit-button state: enabled, label, and what describes a blocked submit. */
function useSubmitGate(text: string) {
  const { isValid, turnstileStatus, submitting, submitError } = useContext(FormContext);
  // Hold the submit until Turnstile has a token (`absent` = no widget, never blocks);
  // otherwise a fast submit lands on the "Verification needed" card and the lead is lost.
  // "Verifying…" only for the first, invisible check: after a rejected submit the label
  // stays put (the alert explains), and a click challenge has its own prompt.
  const verifying = turnstileStatus === 'pending' && !submitError;
  const enabled = !submitting && isValid && (turnstileStatus === 'absent' || turnstileStatus === 'ready');
  const label = submitting ? 'Sending…' : isValid && verifying ? 'Verifying…' : text;
  // While Turnstile blocks the submit, point the button at the reason shown by TurnstileField.
  const describedBy =
    turnstileStatus === 'interactive'
      ? TURNSTILE_PROMPT_ID
      : turnstileStatus === 'error'
        ? TURNSTILE_ERROR_ID
        : undefined;
  return { enabled, label, submitError, describedBy };
}

export function SubmitButton({ text = 'Submit', className = '' }: SubmitButtonProps) {
  const { enabled, label, submitError, describedBy } = useSubmitGate(text);

  return (
    <>
      {/* A rejected in-page submit: same copy as the page's error card, with the form
          (and what the user typed) kept for the retry. */}
      {submitError && (
        <div
          role="alert"
          data-testid="form-error-inline"
          tabIndex={-1}
          className="col-span-2 mt-4 flex flex-col gap-1 rounded-lg border border-[#FF6B6B]/50 bg-[#331B1B] p-4 text-left"
        >
          <p className="font-bold text-white">{submitError.title}</p>
          <p className="text-gray-4">{submitError.body}</p>
        </div>
      )}
      <Button
        type="submit"
        className={cn(`form-animate-in col-span-2 mt-4 place-self-center`, className)}
        disabled={!enabled}
        outline={!enabled}
        aria-describedby={describedBy}
        data-testid="form-submit"
      >
        {label}
      </Button>
    </>
  );
}

interface MessageFieldProps extends FormFieldProps {
  placeholder?: string;
  name?: string;
  rows?: number;
  defaultValue?: string;
}

export function MessageField({
  className = '',
  placeholder = 'Message',
  rows,
  name = formFields.message,
  required = true,
  defaultValue,
}: MessageFieldProps) {
  return (
    <TextArea
      name={name}
      placeholder={placeholder}
      rows={rows}
      defaultValue={defaultValue}
      className={cn(`form-animate-in col-span-2 bg-(--form-input-bg,#2D4453)`, className)}
      required={required}
    />
  );
}
